import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Api from "Vendor/Api";
import { BannerContext } from "Vendor/Context/BannerContextProvider";

type Props = {
    htmlForm?: { __html: string };
    rootSelector?: string;
    disableUserDefinedChoices?: boolean;
};

const escapeHtml = (str: string): string =>
    str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const usePropertyChoiceSelect = ({
    htmlForm,
    rootSelector = "[data-form-root]",
    disableUserDefinedChoices = false,
}: Props) => {
    const { t } = useTranslation();
    const { setBanner } = useContext(BannerContext);

    const creatingRef = useRef<Set<string>>(new Set());
    const createdRef = useRef<Set<string>>(new Set());

    const buildPayload = (term: string) => ({
        translations: {
            en: { label: term },
            fr: { label: term },
        },
        value: term.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    });

    const createTag = (params: any) => {
        const term = $.trim(params.term);
        if (!term) return null;

        const normalized = term.toLowerCase();

        if (createdRef.current.has(normalized)) return null;

        return {
            id: term,
            text: term,
            newTag: true,
            creating: creatingRef.current.has(normalized),
        };
    };

    const renderTag = (data: any) => {
        if (data.id === undefined) return null;

        const safeText = escapeHtml(String(data.text ?? ""));

        if (data.creating) {
            return $(`
                <div class="p-2 text-muted">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    ${t("app.choice.creating_choice", {name: safeText})}
                </div>
            `)[0];
        }

        if (data.newTag) {
            return $(`
                <div class="p-2 text-primary">
                    <i class="fas fa-plus me-2"></i>
                    ${t("app.choice.create_new", { name: safeText })}
                </div>
            `)[0];
        }

        return data.text;
    };

    const getSearchInput = ($el: any): any => {
        const select2 = $el.data("select2");
        if (!select2) return null;

        if (select2.dropdown?.$search?.length) {
            return select2.dropdown.$search;
        }

        const $inline = select2.$container?.find(".select2-search__field");
        return $inline?.length ? $inline : null;
    };

    const refreshDropdown = ($el: any): ReturnType<typeof setTimeout> | null => {
        const $search = getSearchInput($el);
        if (!$search) return null;

        const val = $search.val();
        return setTimeout(() => {
            $search.val(val).trigger("input");
        }, 0);
    };

    const clearSearch = ($el: any): void => {
        const $search = getSearchInput($el);

        if ($search) {
            $search.val("").trigger("input");
        }
    };

    const handleCreateChoice = async ({
        term,
        propertyId,
        $el,
    }: any) => {
        const normalized = term.toLowerCase();

        try {
            creatingRef.current.add(normalized);
            refreshDropdown($el);

            const res = await Api.post(
                `/property/${propertyId}/choice/new`,
                buildPayload(term)
            );

            const newId = res?.data?.id ?? term;
            const newLabel = res?.data?.label ?? term;

            createdRef.current.add(normalized);
            creatingRef.current.delete(normalized);

            const isMultiple = $el.prop("multiple");

            if (isMultiple) {
                const currentVals: string[] = ($el.val() as string[]) || [];
                const option = new Option(newLabel, String(newId), true, true);

                $el.append(option);
                $el.val([...currentVals, String(newId)]).trigger("change");
                clearSearch($el);
            } else {
                const option = new Option(newLabel, String(newId), true, true);
                $el.append(option).val(String(newId)).trigger("change");
                $el.select2("close");
            }

            setBanner({
                type: "success",
                i18nTitle: "app.save",
                i18nMessage: "app.data_saved_message",
            });
        } catch (e) {
            creatingRef.current.delete(normalized);

            setBanner({
                type: "error",
                i18nTitle: "app.save",
                i18nMessage: "app.data_save_error_message",
            });

            refreshDropdown($el);
        }
    };

    useEffect(() => {
        if (!htmlForm) return;

        const root = document.querySelector(rootSelector);
        if (!root) return;

        const initSelect2 = () => {
            $(root).find("[property] .form-select").each(function () {
                const $el = $(this);

                const wrapper = $el.closest("[property]");
                const isMultiple = $el.prop("multiple");

                if ($el.data("select2")) {
                    $el.select2("destroy");
                }

                const config: any = {
                    theme: "bootstrap-5",
                    width: "100%",
                    placeholder: "",
                    allowClear: !isMultiple,
                    closeOnSelect: !isMultiple,
                    dropdownParent: $(root),
                    minimumResultsForSearch: 0,
                };

                const allowCreate =
                    !disableUserDefinedChoices &&
                    wrapper.attr("allow-user-defined-choice") === "true";

                if (allowCreate) {
                    config.tags = true;
                    config.createTag = createTag;
                    config.templateResult = renderTag;

                    $el.off("select2:selecting.userDefined");

                    $el.on("select2:selecting.userDefined", function (e: any) {
                        const data = e.params.args.data;
                        if (!data?.newTag) return;

                        if (creatingRef.current.has(data.text.toLowerCase())) {
                            e.preventDefault();
                            return;
                        }

                        e.preventDefault();

                        const term = data.text;
                        const propertyId = $(this)
                            .closest("[property]")
                            .attr("property_id");

                        if (!propertyId) return;

                        handleCreateChoice({
                            term,
                            propertyId,
                            $el,
                        });
                    });
                }

                $el.select2(config);
                $el.on("select2:clear", function () {
                    setTimeout(() => {
                        $(this).select2("close");
                    }, 0);
                });
            });

            $(".form-select[multiple], .form-select.form-select2")
                .not("[property] .form-select")
                .select2({theme: "bootstrap-5"})
        };

        const initTimer = setTimeout(initSelect2, 0);
        return () => clearTimeout(initTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlForm, disableUserDefinedChoices, rootSelector]);
};

export default usePropertyChoiceSelect;
